import { dequal } from 'dequal';
import React, { memo, useState, useEffect, useMemo, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, IconButton, Tooltip, Typography, Checkbox, CardMedia } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { useInView } from 'react-intersection-observer';

import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import DownloadIcon from '@material-ui/icons/GetAppSharp';
import LockOpenIcon from '@material-ui/icons/LockOpenSharp';
import FavoriteNIcon from '@material-ui/icons/FavoriteBorderSharp';
import FavoriteYIcon from '@material-ui/icons/FavoriteSharp';
import DeleteIcon from '@material-ui/icons/DeleteOutlineSharp';
import QaIcon from '@material-ui/icons/BuildTwoTone';
import AcceptIcon from '@material-ui/icons/CheckSharp';
import { StitchIcon } from './Icons';

import { collectTags, makePseudoMat } from '../utils/materialsLib';
import { AppContext } from './AppState';
import TagList from './TagList';
import ProgressOverlay from './ProgressOverlay';
import { USER_TILING_INCLUDED } from '../utils/users';


const cardWidth = 172;
const cardHeight = 270;
const thumbHeight = 215;

const useStyles = makeStyles((theme) => ({
  galleryImageClickable: {
    cursor: 'pointer',
  },
  card: {
    width: cardWidth,
    height: cardHeight,
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(3),
    position: 'relative',
  },
  cardHeader: {
    color: '#555',
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    zIndex: '1',
    '&:hover': {
      position: 'relative',
      flexWrap: 'wrap',
      overflow: 'visible',
      zIndex: '2',
    },
  },
  cardHeaderSpan: {
    '&:hover': {
      display: 'inline-block',
      paddingRight: '40px !important',
      background: 'linear-gradient(270deg, ' +
        'rgba(250,250,250,0) 0%, ' +
        'rgba(250,250,250,1) 25px, ' +
        'rgba(250,250,250,1) 100%)',
    },
  },
  cardText: {
    color: '#777',
    fontSize: '0.8em',
  },
  cardButton: {
    color: '#ddd',
    width: '35px',
    height: '35px',
    marginLeft: '0px',
    marginRight: '0px',
    '&:hover': {
      color: theme.palette.primary.main,
    },
    '&:focus': {
      outline: 'none',
    },
  },
  cardButtonRight: {
    float: 'right'
  },
  cardIcon: {
    width: '25px',
    height: '25px',
  },
  cardImageContainer: {
    position: 'relative',
    textAlign: 'center',
  },
  cardImageOverlayIcon: {
    position: 'absolute',
    top: '-4px',
    right: '-4px',
  },
  selectOverlay: {
    position: 'absolute',
    top: '-4px',
    left: '-4px',
  },
  checkBox: {
    opacity: '0.85',
    '&:hover': {
      opacity: '1.0',
    },
  },
  checkBoxIcon: {
    color: theme.palette.primary.main,
    backgroundColor: '#fff',
    opacity: '1.0',
  },
  checkBoxOutlineIcon: {
    color: '#ffffffcc',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
  cardImageOverlayButtons: {
    position: 'absolute',
    bottom: '0px',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    height: '35px',
    width: cardWidth,
  },
  viewIcon: {
    width: '20px',
    height: '20px',
  },
  statusText: {
    color: '#ccc',
  },
  statusTextHighlight: {
    color: '#f99',
    fontSize: '1.1em',
    fontWeight: 700,
  },
  spaceBetween: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  acceptIcon: {
    color: '#3fff00',
    fontSize: '2.2em',
  },
}));

function ShareButton(props) {
  const { materialId, onClickPublish, unlocked, processedOrBaking } = props;
  const classes = useStyles();

  const shareButtonTooltip = unlocked ? (
    'Download or share'
   ) : (
    'Unlock for downloading or sharing'
   );

  return <>
    {processedOrBaking && (
      <Tooltip title={shareButtonTooltip}>
        <IconButton
          className={classes.cardButton}
          onClick={() => { onClickPublish(materialId) }}
        >
          {unlocked  && <DownloadIcon className={classes.cardIcon} />}
          {!unlocked && <LockOpenIcon className={classes.cardIcon} />}
        </IconButton>
      </Tooltip>
    )}
  </>;
}

function FavouriteButton (props) {
  const { favourite, onClickFavourite, materialId, processedOrBaking } = props;
  const classes = useStyles();
  return <>
    {processedOrBaking && (
      <Tooltip title={favourite ? 'Unfavourite this material' : 'Favourite this material'}>
        <IconButton
          className={classes.cardButton}
          onClick={() => { onClickFavourite(materialId) }}
        >
          {favourite && (
            <FavoriteYIcon className={classes.cardIcon} />
          )}
          {!favourite && (
            <FavoriteNIcon className={classes.cardIcon} />
          )}
        </IconButton>
      </Tooltip>
    )}
  </>;
}

function ViewStitchButton(props) {
  const { materialUserId, materialId, setStitchViewModalMatProxy, stitched } = props;
  const classes = useStyles();
  return <>
    {stitched && (
      <Tooltip title={'View stitched material details'}>
        <IconButton
          className={classes.cardButton}
          onClick={() => {setStitchViewModalMatProxy({ materialUserId, materialId });}}
        >
          <StitchIcon className={classes.cardIcon} />
        </IconButton>
      </Tooltip>
    )}
  </>;
}

function DeleteButton(props) {
  const { materialId, onClickDelete } = props;
  const classes = useStyles();
  return <>
    <Tooltip title='Delete this material'>
      <IconButton
        className={`${classes.cardButton} ${classes.cardButtonRight}`}
        onClick={() => { onClickDelete(materialId) }}
      >
        <DeleteIcon className={classes.cardIcon} />
      </IconButton>
    </Tooltip>
  </>;
}

function ThumbnailImage(props) {
  const { handleClick, title, url } = props;
  const classes = useStyles();

  const image = url ?? 'BDT_Mark_Digital_WHT.png';
  const opacity = url ? 1.0 : 0.5;

  if (image) {
    return <>
      <CardMedia
        id={image}
        className={classes.galleryImageClickable}
        style={{height: thumbHeight, opacity}}
        image={image}
        title={title}
        onClick={handleClick}
      />
    </>;
  }
}

function ItemButtons(props) {
  const {
    favourite,
    materialUserId,
    materialId,
    onClickDelete,
    onClickFavourite,
    onClickPublish,
    processedOrBaking,
    setStitchViewModalMatProxy,
    stitched,
    unlocked,
    tilingAccepted,
  } = props;
  const classes = useStyles();
  return <>
    <Grid container className={classes.spaceBetween}>
      <Grid item>
        <Grid container>
          <Grid item>
            {tilingAccepted && (
              <Tooltip title='Tiling accepted'>
                <AcceptIcon className={classes.acceptIcon} />
              </Tooltip>
            )}
          </Grid>
          <Grid item>
            <ShareButton
              materialId={materialId}
              onClickPublish={onClickPublish}
              unlocked={unlocked}
              processedOrBaking={processedOrBaking}
            />
            <FavouriteButton
              favourite={favourite}
              materialId={materialId}
              onClickFavourite={onClickFavourite}
              processedOrBaking={processedOrBaking}
            />
            <ViewStitchButton
              materialUserId={materialUserId}
              materialId={materialId}
              setStitchViewModalMatProxy={setStitchViewModalMatProxy}
              stitched={stitched}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <DeleteButton
          materialId={materialId}
          onClickDelete={onClickDelete}
        />
      </Grid>
    </Grid>
  </>;
}

export default function GalleryItemProvider(props) {
  const {
    qaDisplay,
    setGalleryId,
    setRedirect,
    userTags,
   } = useContext(AppContext);

  return <GalleryItemMemo
    { ...props}
    qaDisplay={qaDisplay}
    setGalleryId={setGalleryId}
    setRedirect={setRedirect}
    userTags={userTags}
  />;
}

const GalleryItemMemo = memo(GalleryItemActual, (oldprops, newprops) => dequal(oldprops, newprops));

function GalleryItemActual(props) {
  //
  // Note: GalleryItem visibility is controlled by the visible property.
  //
  const {
    // Material item properties.
    //
    // We break these out into individual properties so that React property change detection works correctly
    // which avoids unnecessary renders and stale renders. This is an important performance optimization
    // because in large material libraries, rendering all the GalleryItems can produce significant visible
    // lag.
    //
    // Any material properties that are not basic types need some kind of enhanced change management, as by
    // default React compares objects by reference ID, which will cause multiple unnecessary renders of all
    // GalleryItems.
    //
    // These properties come from the materials database.
    favourite,
    materialUserId,
    materialId,
    name,
    qaStatus,
    status,
    stitched,
    success,
    tagsAuto,
    tagsUser,
    tiledUserPath,
    tilingIncluded,
    texBakingStatus,
    text,
    unlocked,
    tilingAccepted,

    // Annotated material item properties added by the app.
    visible,

    // Properties from AppContext obtained using a Provider wrapper to avoid rendering all GalleryItems when
    // anything changes in AppContext.
    qaDisplay,
    setGalleryId,
    setRedirect,
    userTags,

    // UI handlers and properties -- we use a controlled component model where both the values and the
    // handlers are passed in from the parent. This means that GalleryItem can focus on display and reduces
    // dependencies that might cause expensive re-renders of all GalleryItems.
    onClickDelete,
    onClickFavourite,
    onClickPublish,
    onTagsChanged,
    onSelect,
    selected,
    setStitchViewModalMatProxy,
    thumbnailUrl,
  } = props;

  const { ref, inView } = useInView({ triggerOnce: true, rootMargin: '200px 0px' });

  const classes = useStyles();
  const [navigatingClick, setNavigatingClick] = useState(false);

  const tagList = useMemo(() => {
    const pseudoMat = makePseudoMat(tagsAuto, tagsUser, tilingIncluded, tiledUserPath);
    return collectTags([ pseudoMat ], USER_TILING_INCLUDED);
  }, [tagsAuto, tagsUser, tiledUserPath, tilingIncluded]);

  // Put this in a useEffect so that the spinny overlay appears while navigating
  useEffect(() => {
    if (navigatingClick) {
      setGalleryId(materialId);
      setRedirect('/view');
    }
  }, [materialId, navigatingClick, setGalleryId, setRedirect]);

  function handleClick() {
    setNavigatingClick(true);
  }

  const processedOrBaking = (
    status === 'processed' ||
    (texBakingStatus === 'processing')
  );

  const displayThumbnail = (materialId && thumbnailUrl && (
    status === 'processed' && success ||
    (qaStatus === 'qa' && success && qaDisplay) ||
    (texBakingStatus === 'processing' && success)
  ));

  const title = name;
  const description = text ? `[ ${text} ]` : <>&nbsp;</>;
  const attachUrl = displayThumbnail ? thumbnailUrl : null;

  return (<>
    {visible && (<>
      <Grid item ref={ref} className={classes.card}>
        {!inView && (
          <Skeleton variant='rect' width={cardWidth} height={cardHeight} />
        )}
        {inView && (<>
          <div className={classes.cardImageContainer}>
            <ThumbnailImage
              handleClick={handleClick}
              title={title}
              url={attachUrl}
            />
            <div className={classes.selectOverlay}>
              <Checkbox className={classes.checkBox}
                checked={selected}
                checkedIcon={<CheckBoxIcon className={classes.checkBoxIcon} />}
                icon={<CheckBoxOutlineIcon className={classes.checkBoxOutlineIcon} />}
                onClick={(event) => onSelect(materialId, event)}
              />
            </div>

            <div className={classes.cardImageOverlayIcon}>
              <Tooltip title='View or edit this material'>
                <IconButton onClick={handleClick}>
                  {qaStatus === 'qa' ? (
                    <QaIcon color='primary' />
                  ) : (
                    <img src='viewIcon.png' className={classes.viewIcon} />
                  )}
                </IconButton>
              </Tooltip>
            </div>
            <div className={classes.cardImageOverlayButtons}>
              <ItemButtons
                favourite={favourite}
                materialUserId={materialUserId}
                materialId={materialId}
                onClickDelete={onClickDelete}
                onClickFavourite={onClickFavourite}
                onClickPublish={onClickPublish}
                processedOrBaking={processedOrBaking}
                setStitchViewModalMatProxy={setStitchViewModalMatProxy}
                stitched={stitched}
                unlocked={unlocked}
                tilingAccepted={tilingAccepted}
              />
            </div>
            {navigatingClick && <ProgressOverlay />}
          </div>
          <Typography noWrap variant='h4' className={classes.cardHeader}>
            <span className={classes.cardHeaderSpan}>
              {title}
            </span>
          </Typography>
          {/* When variant='caption', noWrap stops working.  Why? */}
          {tagList.length == 0 && (
            <Grid container>
              <Grid item xs={10}>
                <Typography noWrap className={classes.cardText}>
                  {description}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <TagList
                  materialId={materialId}
                  tags={tagList}
                  onChange={onTagsChanged}
                  tagChoices={userTags}
                />
              </Grid>
            </Grid>
          )}
          {tagList.length > 0 && (
            <TagList
              materialId={materialId}
              tags={tagList}
              onChange={onTagsChanged}
              tagChoices={userTags}
            />
          )}
        </>)}
      </Grid>
    </>)}
  </>);
}
